/**
 * useProjectEvents — the COMMS rail's feed: newest-first, cursor-paginated
 * project events (GET /projects/:id/events), live-polled.
 *
 * Polling refreshes the FIRST page only (react-query's own refetchInterval on
 * an infinite query re-walks EVERY loaded page — N requests per tick once the
 * user has scrolled). We poll page 1 ourselves every 5s while the hook is
 * enabled and the document is visible, splice it in as pages[0], and let
 * mergeEventPages() dedupe by id — ids are unique + lexically time-ordered.
 *
 * Key: ['projectEvents', projectId, uuidsKey, kind] — uuidsKey is the sorted,
 * comma-joined scope so [a,b] and [b,a] share a cache entry.
 */
import { useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { listProjectEvents } from '../services/projectEvents';
import { mergeEventPages } from '../utils/projectEvents';

export { mergeEventPages };

export const PROJECT_EVENTS_PAGE = 30;
export const PROJECT_EVENTS_POLL_MS = 5000;

export const projectEventsKey = (projectId, uuidsKey, kind) => ['projectEvents', projectId, uuidsKey || '', kind || ''];

const uuidsKeyOf = (scopeUuids) => (Array.isArray(scopeUuids) && scopeUuids.length
  ? [...new Set(scopeUuids.filter((u) => typeof u === 'string' && u))].sort().join(',')
  : '');

/**
 * @param {string} projectId
 * @param {object} [opts]
 * @param {string[]|null} [opts.scopeUuids]  agent + member uuids; null/[] = whole project
 * @param {string} [opts.kind]
 * @param {boolean} [opts.enabled=true]     false while the rail is closed — no polling
 */
export function useProjectEvents(projectId, { scopeUuids = null, kind = '', enabled = true } = {}) {
  const queryClient = useQueryClient();
  const uuidsKey = uuidsKeyOf(scopeUuids);
  const workflowUuid = uuidsKey ? uuidsKey.split(',') : undefined;
  const queryKey = useMemo(() => projectEventsKey(projectId, uuidsKey, kind), [projectId, uuidsKey, kind]);
  const on = !!projectId && !!enabled;

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => listProjectEvents(projectId, {
      limit: PROJECT_EVENTS_PAGE, cursor: pageParam || undefined, workflowUuid, kind: kind || undefined,
    }),
    initialPageParam: undefined,
    getNextPageParam: (last) => last?.nextCursor || undefined,
    enabled: on,
    staleTime: PROJECT_EVENTS_POLL_MS,
    refetchOnWindowFocus: false,
  });

  // First-page-only poll (see header). Skipped while the tab is hidden; the
  // visibilitychange listener fires one tick on return so the rail catches up.
  useEffect(() => {
    if (!on) return undefined;
    let cancelled = false;
    let inFlight = false;
    const tick = async () => {
      if (cancelled || inFlight) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        const page = await listProjectEvents(projectId, {
          limit: PROJECT_EVENTS_PAGE, workflowUuid, kind: kind || undefined,
        });
        if (cancelled) return;
        queryClient.setQueryData(queryKey, (old) => {
          if (!old || !Array.isArray(old.pages) || !old.pages.length) return old;
          return {
            ...old,
            pages: [page, ...old.pages.slice(1)],
            pageParams: [undefined, ...old.pageParams.slice(1)],
          };
        });
      } catch {
        // fail-soft: the next tick retries; the existing pages stay on screen
      } finally {
        inFlight = false;
      }
    };
    const id = setInterval(tick, PROJECT_EVENTS_POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // workflowUuid is derived from uuidsKey (already in queryKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, projectId, uuidsKey, kind, queryClient, queryKey]);

  const pages = query.data?.pages;
  const events = useMemo(() => mergeEventPages(pages), [pages]);
  const first = pages?.[0];

  return {
    ...query,
    queryKey,
    events,
    runningCount: Number(first?.running?.count) || 0,
    needsYou: first?.needsYou || null,
  };
}

export default useProjectEvents;
